/*  ============================================================================
    AddTimeModal.jsx — log time against an EXISTING Autotask ticket, from a
    Missing Time row. Autotask → "Missing Time" → "Add time to ticket".

    Ticket comes from the row's candidate list or a typed ticket number
    (/api/time-reconcile/ticket-by-number). Once chosen, its detail is fetched
    from /api/timelog/ticket/:id for the contract and status.

    ⚠ CROSS-CLIENT GUARD: a ticket belonging to a different company than the
      row's company is allowed, but only behind an explicit tick. The warning
      names both companies. Nothing is submitted until it is ticked.

    ⚠ STATUS New (1): the backend's /api/timelog/entry has NO status check
      (Step 0, 2026-10-27) — the "can't log against New" rule lives only in the
      mobile app's TimeScreen, and Autotask itself is what refuses. So this
      modal WARNS when the ticket is New, submits anyway, and shows Autotask's
      rejection verbatim. It never changes a ticket's status silently.

    Submits to POST /api/timelog/entry (the mobile time-logger's route), which
    resolves the Autotask resource from the login email — the entry is always
    Craig's. Start/end are the meeting's times in Perth local, converted to UTC
    once here (localToUtcNoZ); dateWorked is the local calendar date.
    ============================================================================ */

import { useState, useEffect, useMemo } from "react";
import { findTicketByNumber, getMyRoles } from "../../api/timeReconcileApi";
import { getTicketDetail, createTimeEntry } from "../../api/ticketApi";
import { buildAddTimePrefill, hoursBetween, localToUtcNoZ } from "./reconcileRules";

const NEW_STATUS_ID = 1;
const DEFAULT_WORK_TYPE_NAME = "Remote Support";

const errMsg = (e) =>
  e?.response?.data?.error || e?.response?.data?.message || (e?.response?.status ? `HTTP ${e.response.status}` : (e?.message || "request failed"));

const note = { fontSize: 10.5, color: "var(--text3)", lineHeight: 1.5 };

export default function AddTimeModal({ item, atReference, onClose, onLogged }) {
  const prefill = useMemo(() => buildAddTimePrefill(item), [item]);
  const candidates = item?.coverage?.candidateTickets || [];

  const [ticket, setTicket] = useState(null);          // { id, ticketNumber, title, companyID, status, statusName }
  const [detail, setDetail] = useState(null);          // raw AT ticket (contractID, status, companyID)
  const [detailLoading, setDetailLoading] = useState(false);
  const [numberInput, setNumberInput] = useState("");
  const [finding, setFinding] = useState(false);
  const [findError, setFindError] = useState("");
  const [crossOk, setCrossOk] = useState(false);

  const [dateLocal, setDateLocal] = useState(prefill.dateLocal || "");
  const [startLocal, setStartLocal] = useState(prefill.startLocal || "");
  const [endLocal, setEndLocal] = useState(prefill.endLocal || "");
  const [hours, setHours] = useState(prefill.hours || "");
  const [hoursTouched, setHoursTouched] = useState(false);
  const [summaryNotes, setSummaryNotes] = useState(prefill.summaryNotes || "");

  const workTypes = atReference?.workTypes || [];
  const defaultWorkType = workTypes.find((w) => new RegExp(`^\\s*${DEFAULT_WORK_TYPE_NAME}\\s*$`, "i").test(w.name || w.label || ""));
  const [workTypeId, setWorkTypeId] = useState(defaultWorkType?.id ?? "");
  const [roles, setRoles] = useState(null);
  const [roleId, setRoleId] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Roles: default first, per /api/timelog/my-roles. A failure leaves "default role".
  useEffect(() => {
    let cancelled = false;
    getMyRoles().then((r) => { if (!cancelled) { setRoles(r); if (r[0]?.roleID != null) setRoleId(String(r[0].roleID)); } })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, []);

  // Hours follow start/end until Craig edits hours by hand.
  useEffect(() => {
    if (!hoursTouched) setHours(hoursBetween(startLocal, endLocal));
  }, [startLocal, endLocal, hoursTouched]);

  // Ticket detail (contract + status + company) once a ticket is chosen.
  useEffect(() => {
    if (!ticket?.id) { setDetail(null); return undefined; }
    let cancelled = false;
    setDetailLoading(true);
    getTicketDetail(ticket.id)
      .then((t) => { if (!cancelled) setDetail(t || null); })
      .catch((e) => { if (!cancelled) { setDetail(null); setSubmitError(`Could not load ticket: ${errMsg(e)}`); } })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [ticket?.id]);

  const chooseCandidate = (id) => {
    setCrossOk(false); setSubmitError("");
    const c = candidates.find((t) => String(t.id) === String(id));
    setTicket(c ? { id: c.id, ticketNumber: c.ticketNumber, title: c.title, companyID: c.companyID, status: c.status, statusName: c.statusName } : null);
  };

  const findByNumber = async () => {
    const num = numberInput.trim();
    if (!num) return;
    setFinding(true); setFindError(""); setCrossOk(false); setSubmitError("");
    try {
      const t = await findTicketByNumber(num);
      setTicket(t);
    } catch (e) {
      setTicket(null);
      setFindError(e?.response?.status === 404 ? `No ticket ${num.toUpperCase()}` : errMsg(e));
    } finally { setFinding(false); }
  };

  const ticketCompanyId = detail?.companyID ?? ticket?.companyID ?? null;
  const rowCompanyId = item?.company?.id ?? null;
  const crossCompany = ticket && rowCompanyId != null && ticketCompanyId != null && String(ticketCompanyId) !== String(rowCompanyId);
  const ticketStatus = detail?.status ?? ticket?.status ?? null;
  const isNew = ticketStatus != null && String(ticketStatus) === String(NEW_STATUS_ID);
  const hoursNum = Number(hours) || 0;

  const canSubmit = !!ticket && !detailLoading && !submitting && hoursNum > 0 && summaryNotes.trim() && (!crossCompany || crossOk)
    && dateLocal && startLocal && endLocal;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setSubmitError("");
    try {
      const payload = {
        ticketID: ticket.id,
        hoursWorked: hoursNum,
        summaryNotes: summaryNotes.trim(),
        billingCodeID: workTypeId || null,
        contractID: detail?.contractID || null,
        roleID: roleId || null,
        startDateTime: localToUtcNoZ(dateLocal, startLocal),
        endDateTime: localToUtcNoZ(dateLocal, endLocal),
        dateWorked: dateLocal,
      };
      await createTimeEntry(payload);
      onLogged?.({ ticketId: ticket.id, ticketNumber: ticket.ticketNumber, hours: hoursNum });
    } catch (e) {
      setSubmitError(`Autotask rejected the time entry: ${errMsg(e)}${isNew ? " — the ticket is still New; change its status in Autotask and try again." : ""}`);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="at-overlay" onClick={(e) => { if (e.target.className === "at-overlay") onClose?.(); }}>
      <div className="at-modal" role="dialog" aria-label="Add time to ticket">
        <div className="at-modal-hdr">
          <div className="at-modal-title">⏱ Add time to ticket</div>
          <div className="at-modal-sub">
            <span>{item.subject}</span><span>·</span><span>{item.dateLocal} {item.startLocal}–{item.endLocal}</span>
            {item.company && <><span>·</span><span style={{ color: "var(--ops)" }}>{item.company.name}</span></>}
          </div>
        </div>
        <div className="at-modal-body">
          <div className="at-section-label">🎫 Ticket</div>
          <div className="at-field">
            <div className="at-label">Candidate tickets {item.company ? `for ${item.company.name}` : ""}</div>
            <select className="at-input" aria-label="Candidate ticket" value={ticket && candidates.some((c) => String(c.id) === String(ticket.id)) ? String(ticket.id) : ""}
              onChange={(e) => chooseCandidate(e.target.value)}>
              <option value="">— {candidates.length ? "Choose a ticket" : "No recent tickets for this company"} —</option>
              {candidates.map((t) => (
                <option key={t.id} value={String(t.id)}>{t.ticketNumber} · {t.title}{t.statusName ? ` (${t.statusName})` : ""}</option>
              ))}
            </select>
          </div>
          <div className="at-field">
            <div className="at-label">Or find by ticket number</div>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="at-input" aria-label="Ticket number" placeholder="T20261001.0001" value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") findByNumber(); }} />
              <button type="button" className="btn" onClick={findByNumber} disabled={finding || !numberInput.trim()}>{finding ? "Finding…" : "Find"}</button>
            </div>
            {findError && <div style={{ ...note, color: "var(--danger)", marginTop: 4 }}>{findError}</div>}
          </div>

          {ticket && (
            <div data-testid="chosen-ticket" style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 11.5 }}>
              <div><strong>{ticket.ticketNumber}</strong> · {ticket.title}</div>
              <div style={note}>
                {detailLoading ? "Loading contract and status…" : (
                  <>Status: {ticket.statusName || detail?.status || "?"} · Contract: {detail?.contractID ? `#${detail.contractID}` : "none on ticket (Autotask will infer)"}</>
                )}
              </div>
              {isNew && (
                <div data-testid="new-status-warning" style={{ ...note, color: "var(--warn)", marginTop: 4 }}>
                  ⚠ This ticket is <strong>New</strong>. Autotask may refuse time against a New ticket; if it does, the error is shown here and nothing is changed on the ticket.
                </div>
              )}
              {crossCompany && (
                <div data-testid="cross-company-warning" style={{ marginTop: 6, padding: "8px 10px", borderRadius: 6, background: "rgba(230,126,34,0.12)", border: "1px solid rgba(230,126,34,0.4)", fontSize: 11 }}>
                  <div style={{ fontWeight: 600, color: "var(--warn)" }}>⚠ Different company</div>
                  <div style={{ marginTop: 2 }}>This ticket belongs to company #{String(ticketCompanyId)}, but this meeting is for <strong>{item.company?.name}</strong> (#{String(rowCompanyId)}). Time logged here bills the ticket's company.</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, cursor: "pointer" }}>
                    <input type="checkbox" aria-label="I understand this is a different company" checked={crossOk} onChange={(e) => setCrossOk(e.target.checked)} />
                    <span>I understand — log it against this ticket anyway</span>
                  </label>
                </div>
              )}
            </div>
          )}

          <div className="at-section-label">⏱ Time</div>
          <div className="at-row-3">
            <div className="at-field">
              <div className="at-label">Date</div>
              <input type="date" className="at-input" aria-label="Date" value={dateLocal} onChange={(e) => setDateLocal(e.target.value)} />
            </div>
            <div className="at-field">
              <div className="at-label">Start</div>
              <input type="time" className="at-input" aria-label="Start" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
            </div>
            <div className="at-field">
              <div className="at-label">End</div>
              <input type="time" className="at-input" aria-label="End" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
            </div>
          </div>
          <div className="at-row-3">
            <div className="at-field">
              <div className="at-label">Hours</div>
              <input type="number" className="at-input" aria-label="Hours" min="0" max="24" step="0.25" value={hours}
                onChange={(e) => { setHoursTouched(true); setHours(e.target.value === "" ? "" : +e.target.value); }} />
            </div>
            <div className="at-field">
              <div className="at-label">Work Type</div>
              <select className="at-input" aria-label="Work type" value={workTypeId} onChange={(e) => setWorkTypeId(e.target.value)}>
                <option value="">— default —</option>
                {workTypes.map((w) => <option key={w.id} value={w.id}>{w.name || w.label}</option>)}
              </select>
            </div>
            <div className="at-field">
              <div className="at-label">Role</div>
              <select className="at-input" aria-label="Role" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                <option value="">{roles === null ? "Loading your roles…" : "— default role —"}</option>
                {(roles || []).map((r) => <option key={r.roleID} value={String(r.roleID)}>{r.name}</option>)}
              </select>
            </div>
          </div>
          <div className="at-field">
            <div className="at-label">Summary Notes</div>
            <textarea className="at-input" aria-label="Summary notes" rows={3} value={summaryNotes} onChange={(e) => setSummaryNotes(e.target.value)} />
          </div>
          {submitError && <div data-testid="submit-error" style={{ color: "var(--danger)", fontSize: 11, marginBottom: 8 }}>{submitError}</div>}
        </div>
        <div className="at-modal-footer">
          <button type="button" className="btn btn-O" style={{ flex: 1, justifyContent: "center", padding: 9 }} disabled={!canSubmit} onClick={submit}>
            {submitting ? "Logging time…" : `Log ${hoursNum || 0}h to ${ticket?.ticketNumber || "ticket"}`}
          </button>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
